<div class="wrapper-visitor-intelligence">

  <!-- Header & Title -->
  <div class="section-title keep-in-page">
    <div class="d-flex align-items-center justify-content-between flex-wrap gap-3">
      <div class="d-flex align-items-center gap-3">
        <div class="header-icon-box">
          <i class="fi fi-rr-eye"></i>
        </div>
        <div>
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <h1 class="title mb-0">{{ "Visitor Intelligence" | translate }}</h1>
            <span class="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-1 rounded-pill font-semibold header-suite-badge">
              <i class="fi fi-rr-radar me-1"></i>
              {{ "Behavior Analytics Suite" | translate }}
            </span>
          </div>
          <p class="subtitle text-muted mb-0 mt-1">
            {{ "Understand shopper journeys, detect checkout friction, and visualize real user behavior with session recordings and click heatmaps." | translate }}
          </p>
        </div>
      </div>
      <!-- Toggle Button for Visitor Intelligence -->
      <div class="vi-header-toggle-wrap">
        <div class="vi-toggle-card" [class.is-disabled]="!sessionReplayEnabled">
          <div class="vi-toggle-text text-right">
            <span class="vi-toggle-label">{{ "Session Recording" | translate }}</span>
            <div class="vi-toggle-state" [class.state-on]="sessionReplayEnabled" [class.state-off]="!sessionReplayEnabled">
              <span class="vi-status-dot"></span>
              <span class="vi-status-name">{{ (sessionReplayEnabled ? "Active" : "Disabled") | translate }}</span>
            </div>
          </div>
          <div class="section-switch">
            <label class="switch mb-0" [class.disabled]="isSessionReplayLoading || isInitialLoading">
              <input
                type="checkbox"
                id="visitorIntelligenceToggle"
                [checked]="sessionReplayEnabled"
                [disabled]="isSessionReplayLoading || isInitialLoading"
                (click)="toggleSessionReplay($event)"
                [attr.aria-label]="'Toggle Session Replay' | translate"
              />
              <span class="slider round"></span>
            </label>
          </div>
          <div class="spinner-border spinner-border-sm text-primary vi-toggle-spinner" role="status" *ngIf="isSessionReplayLoading">
            <span class="sr-only">Loading...</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Inactive Warning Banner -->
  <div class="alert alert-warning d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3 mb-0 vi-disabled-alert" *ngIf="!sessionReplayEnabled && !isInitialLoading">
    <div class="d-flex align-items-center vi-alert-info">
      <i class="fi fi-rr-exclamation mr-2 vi-alert-icon"></i>
      <div>
        <strong>{{ "Session recording is currently disabled." | translate }}</strong>
        <span class="text-muted ml-1">{{ "Visitor sessions and interactions are not being recorded on your storefront." | translate }}</span>
      </div>
    </div>
    <button type="button" class="btn btn-warning btn-sm font-weight-bold d-inline-flex align-items-center vi-alert-action" [disabled]="isSessionReplayLoading" (click)="enableSessionReplay()">
      <i class="fi fi-rr-play mr-1"></i>
      <span>{{ "Enable Recording" | translate }}</span>
    </button>
  </div>

  <!-- Main Intelligence Tools Grid -->
  <div class="section-content mt-4">
    <div class="row g-4">

      <!-- Card 1: Session Replay -->
      <div class="col-12 col-lg-6">
        <div class="vi-feature-card vi-card--replay" (click)="goToSessionReplay()" role="button" tabindex="0">
          <div class="vi-card-top">
            <div class="vi-icon-wrapper bg-gradient-orange">
              <i class="fi fi-rr-play-alt"></i>
            </div>
            <div class="vi-card-tag">
              <span class="badge rounded-pill bg-orange-subtle text-orange border border-orange-subtle" *ngIf="sessionReplayEnabled">
                <i class="fi fi-rr-video-camera-alt me-1 mr-1"></i> {{ "Session Recordings" | translate }}
              </span>
              <span class="badge rounded-pill bg-secondary-subtle text-muted border border-secondary-subtle" *ngIf="!sessionReplayEnabled">
                <i class="fi fi-rr-pause me-1 mr-1"></i> {{ "Recording Paused" | translate }}
              </span>
            </div>
          </div>

          <div class="vi-card-body">
            <h2 class="vi-card-title">{{ "Session Replay" | translate }}</h2>
            <p class="vi-card-tagline text-muted">
              {{ "Watch step-by-step video playback of actual visitor journeys." | translate }}
            </p>
            <p class="vi-card-description">
              {{ "Replay exactly how customers navigate your store in real-time. Uncover where users click, scroll, hesitate, experience rage clicks, or abandon their shopping cart." | translate }}
            </p>

            <!-- Feature Capabilities List -->
            <div class="vi-features-list">
              <div class="feature-item">
                <i class="fi fi-rr-check text-success"></i>
                <span>{{ "Full mouse movement, click & scroll playback" | translate }}</span>
              </div>
              <div class="feature-item">
                <i class="fi fi-rr-check text-success"></i>
                <span>{{ "Frustration & Rage Click detection" | translate }}</span>
              </div>
              <div class="feature-item">
                <i class="fi fi-rr-check text-success"></i>
                <span>{{ "Mobile, Tablet & Desktop responsive player" | translate }}</span>
              </div>
              <div class="feature-item">
                <i class="fi fi-rr-check text-success"></i>
                <span>{{ "Interactive activity feed & timeline scrubber" | translate }}</span>
              </div>
            </div>
          </div>

          <!-- Card Footer CTA -->
          <div class="vi-card-footer">
            <div class="use-case-badge">
              <span class="text-muted"><i class="fi fi-rr-info me-1"></i> {{ "Best for:" | translate }}</span>
              <strong>{{ "Diagnosing checkout issues & debugging drop-offs" | translate }}</strong>
            </div>
            <button type="button" class="btn btn-primary-custom btn-sm-custom">
              <span>{{ "Launch Session Replay" | translate }}</span>
              <i class="fi fi-rr-arrow-right ms-2 arrow-icon"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Card 2: Heatmap -->
      <div class="col-12 col-lg-6">
        <div class="vi-feature-card vi-card--heatmap" (click)="goToHeatmap()" role="button" tabindex="0">
          <div class="vi-card-top">
            <div class="vi-icon-wrapper bg-gradient-blue">
              <i class="fi fi-rr-cursor"></i>
            </div>
            <div class="vi-card-tag">
              <span class="badge rounded-pill bg-blue-subtle text-primary border border-blue-subtle">
                <i class="fi fi-rr-flame me-1"></i> {{ "Visual Interaction Map" | translate }}
              </span>
            </div>
          </div>

          <div class="vi-card-body">
            <h2 class="vi-card-title">{{ "Heatmap" | translate }}</h2>
            <p class="vi-card-tagline text-muted">
              {{ "Visualize aggregated click density and user attention patterns." | translate }}
            </p>
            <p class="vi-card-description">
              {{ "See visual hot spots of where hundreds of visitors click, tap, and interact on your landing and checkout pages. Discover what catches shoppers' attention and what gets ignored." | translate }}
            </p>

            <!-- Feature Capabilities List -->
            <div class="vi-features-list">
              <div class="feature-item">
                <i class="fi fi-rr-check text-primary"></i>
                <span>{{ "Aggregated click density & hot spot radar" | translate }}</span>
              </div>
              <div class="feature-item">
                <i class="fi fi-rr-check text-primary"></i>
                <span>{{ "Identify high-converting vs unclicked CTA buttons" | translate }}</span>
              </div>
              <div class="feature-item">
                <i class="fi fi-rr-check text-primary"></i>
                <span>{{ "Device-specific comparison (Mobile vs Desktop)" | translate }}</span>
              </div>
              <div class="feature-item">
                <i class="fi fi-rr-check text-primary"></i>
                <span>{{ "Hourly aggregated behavioral updates" | translate }}</span>
              </div>
            </div>
          </div>

          <!-- Card Footer CTA -->
          <div class="vi-card-footer">
            <div class="use-case-badge">
              <span class="text-muted"><i class="fi fi-rr-info me-1"></i> {{ "Best for:" | translate }}</span>
              <strong>{{ "Landing page optimization & CTA placement testing" | translate }}</strong>
            </div>
            <button type="button" class="btn btn-outline-primary-custom btn-sm-custom">
              <span>{{ "Explore Click Heatmaps" | translate }}</span>
              <i class="fi fi-rr-arrow-right ms-2 arrow-icon"></i>
            </button>
          </div>
        </div>
      </div>

    </div>

    <!-- Guide: When to use Session Replay vs Heatmap -->
    <div class="vi-guide-card mt-4">
      <div class="row align-items-center">
        <div class="col-12 col-md-auto mb-3 mb-md-0">
          <div class="guide-icon-box">
            <i class="fi fi-rr-bulb"></i>
          </div>
        </div>
        <div class="col-12 col-md">
          <h3 class="guide-title mb-1">{{ "When to use Replays vs Heatmaps?" | translate }}</h3>
          <p class="guide-desc mb-0 text-muted">
            {{ "Use" | translate }} <strong class="text-dark">{{ "Session Replay" | translate }}</strong> {{ "for micro-level analysis to see individual customer struggles, bugs, and form inputs." | translate }}
            {{ "Use" | translate }} <strong class="text-dark">{{ "Heatmap" | translate }}</strong> {{ "for macro-level analysis to evaluate overall page engagement, button popularity, and layout effectiveness across all visitors." | translate }}
          </p>
        </div>
        <div class="col-12 col-md-auto mt-3 mt-md-0 d-flex gap-2">
          <button type="button" class="btn btn-light btn-sm border" (click)="goToSessionReplay()">
            <i class="fi fi-rr-play me-1 text-orange"></i> {{ "Replays" | translate }}
          </button>
          <button type="button" class="btn btn-light btn-sm border" (click)="goToHeatmap()">
            <i class="fi fi-rr-flame me-1 text-primary"></i> {{ "Heatmap" | translate }}
          </button>
        </div>
      </div>
    </div>

  </div>

</div>
